import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Filter, X, ChevronDown } from 'lucide-react';
import { useProducts } from '../contexts/ProductsContext';
import ProductCard from '../components/ProductCard';

const SORT_OPTIONS = [
  { label: 'Newest',        value: 'newest' },
  { label: 'Price: Low',    value: 'price_asc' },
  { label: 'Price: High',   value: 'price_desc' },
  { label: 'Featured',      value: 'featured' },
];

const PRICE_RANGES = [
  { label: 'All Prices', min: null, max: null },
  { label: 'Under ₹299',  min: null, max: 299  },
  { label: '₹299–₹499',   min: 299,  max: 499  },
  { label: 'Above ₹499',  min: 499,  max: null },
];

export default function ShopPage() {
  const { products } = useProducts();

  const [category,  setCategory]  = useState('All');
  const [priceIdx,  setPriceIdx]  = useState(0);
  const [sort,      setSort]      = useState('newest');
  const [showFilter,setShowFilter]= useState(false);
  const [collSlug,  setCollSlug]  = useState('');

  // All filtering/sorting is pure JS — zero network wait
  const visible = useMemo(() => {
    let list = products.filter(p => p.is_visible !== false);
    if (category !== 'All') list = list.filter(p => p.category === category);
    if (collSlug)           list = list.filter(p => p.collection === collSlug);
    const pr = PRICE_RANGES[priceIdx];
    if (pr.min !== null)    list = list.filter(p => p.price >= pr.min);
    if (pr.max !== null)    list = list.filter(p => p.price <= pr.max);
    switch (sort) {
      case 'price_asc':  list = [...list].sort((a,b) => a.price - b.price); break;
      case 'price_desc': list = [...list].sort((a,b) => b.price - a.price); break;
      case 'featured':   list = [...list].sort((a,b) => (b.is_featured?1:0)-(a.is_featured?1:0)); break;
      default:           list = [...list].sort((a,b) => (b.created_at||'').localeCompare(a.created_at||'')); break;
    }
    return list;
  }, [products, category, priceIdx, sort, collSlug]);

  const categories = ['All', ...Array.from(new Set(products.map(p => p.category).filter(Boolean)))];
  const collections = Array.from(new Set(products.map(p => p.collection).filter(Boolean)));

  return (
    <main data-testid="shop-page" className="pt-20 min-h-screen bg-[#F6F3EE]">
      {/* Header */}
      <div className="border-b border-[#DDD7CE] px-4 md:px-12 lg:px-24 py-6 md:py-10">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-1">The Edit</p>
          <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-[#1E1C1A]">Shop All Pieces</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 py-6 md:py-10">
        {/* Filter bar */}
        <div className="flex items-center justify-between mb-6 md:mb-8 gap-3 flex-wrap">
          {/* Category pills — desktop */}
          <div className="hidden md:flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button key={cat} onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] border transition-colors ${category === cat ? 'bg-[#1E1C1A] text-[#F6F3EE] border-[#1E1C1A]' : 'border-[#DDD7CE] text-[#7A7369] hover:border-[#3A3632]'}`}>
                {cat}
              </button>
            ))}
          </div>

          {/* Mobile filter toggle */}
          <button className="md:hidden flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] border border-[#DDD7CE] px-3 py-1.5" onClick={() => setShowFilter(v => !v)}>
            <Filter size={12} /> Filters
          </button>

          <div className="flex items-center gap-2 ml-auto">
            {/* Sort */}
            <div className="relative">
              <select value={sort} onChange={e => setSort(e.target.value)}
                className="appearance-none bg-transparent border border-[#DDD7CE] text-[10px] uppercase tracking-[0.12em] text-[#7A7369] px-3 py-1.5 pr-7 cursor-pointer outline-none">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7A7369] pointer-events-none" />
            </div>
            <span className="text-[10px] text-[#7A7369] tracking-wide">{visible.length} piece{visible.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Mobile filter panel */}
        {showFilter && (
          <div className="md:hidden mb-6 border border-[#DDD7CE] p-4 bg-[#FAFAF8]">
            <div className="flex justify-between items-center mb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#3A3632]">Filters</span>
              <button onClick={() => setShowFilter(false)}><X size={14} className="text-[#7A7369]" /></button>
            </div>
            <div className="mb-4">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#C6A978] mb-2">Category</p>
              <div className="flex flex-wrap gap-1.5">
                {categories.map(cat => (
                  <button key={cat} onClick={() => { setCategory(cat); setShowFilter(false); }}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border ${category === cat ? 'bg-[#1E1C1A] text-[#F6F3EE] border-[#1E1C1A]' : 'border-[#DDD7CE] text-[#7A7369]'}`}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#C6A978] mb-2">Collection</p>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setCollSlug('')}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border ${!collSlug ? 'bg-[#1E1C1A] text-[#F6F3EE] border-[#1E1C1A]' : 'border-[#DDD7CE] text-[#7A7369]'}`}>
                  All
                </button>
                {collections.map(c => (
                  <button key={c} onClick={() => { setCollSlug(c); setShowFilter(false); }}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border ${collSlug === c ? 'bg-[#1E1C1A] text-[#F6F3EE] border-[#1E1C1A]' : 'border-[#DDD7CE] text-[#7A7369]'}`}>
                    {c.replace(/-/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#C6A978] mb-2">Price</p>
              <div className="flex flex-wrap gap-1.5">
                {PRICE_RANGES.map((pr, i) => (
                  <button key={i} onClick={() => { setPriceIdx(i); setShowFilter(false); }}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border ${priceIdx === i ? 'bg-[#1E1C1A] text-[#F6F3EE] border-[#1E1C1A]' : 'border-[#DDD7CE] text-[#7A7369]'}`}>
                    {pr.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Desktop sidebar + grid */}
        <div className="flex gap-8">
          {/* Sidebar — desktop */}
          <aside className="hidden md:block w-44 flex-shrink-0 pt-1">
            <div className="mb-6">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#C6A978] mb-2.5">Collection</p>
              <div className="flex flex-col gap-1">
                <button onClick={() => setCollSlug('')}
                  className={`text-left text-[11px] tracking-wide py-0.5 transition-colors ${!collSlug ? 'text-[#1E1C1A] font-medium' : 'text-[#7A7369] hover:text-[#3A3632]'}`}>
                  All Collections
                </button>
                {collections.map(c => (
                  <button key={c} onClick={() => setCollSlug(c === collSlug ? '' : c)}
                    className={`text-left text-[11px] tracking-wide py-0.5 transition-colors capitalize ${collSlug === c ? 'text-[#1E1C1A] font-medium' : 'text-[#7A7369] hover:text-[#3A3632]'}`}>
                    {c.replace(/-/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#C6A978] mb-2.5">Price</p>
              <div className="flex flex-col gap-1">
                {PRICE_RANGES.map((pr, i) => (
                  <button key={i} onClick={() => setPriceIdx(i)}
                    className={`text-left text-[11px] tracking-wide py-0.5 transition-colors ${priceIdx === i ? 'text-[#1E1C1A] font-medium' : 'text-[#7A7369] hover:text-[#3A3632]'}`}>
                    {pr.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Product grid */}
          <div className="flex-1">
            {visible.length === 0 ? (
              <div className="text-center py-20">
                <p className="font-['Cormorant_Garamond'] text-2xl font-light text-[#3A3632] mb-2">No pieces found</p>
                <p className="text-sm text-[#7A7369]">Try adjusting your filters.</p>
                <button onClick={() => { setCategory('All'); setPriceIdx(0); setCollSlug(''); setSort('newest'); }}
                  className="mt-4 text-[10px] uppercase tracking-[0.15em] border border-[#DDD7CE] px-4 py-2 text-[#7A7369] hover:border-[#3A3632] transition-colors">
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
                {visible.map((product, i) => (
                  <ProductCard key={product.id} product={product} index={i} thumbWidth={480}
                    sizes="(max-width:768px) 50vw, (max-width:1200px) 33vw, 25vw"
                    data-testid={`shop-product-${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}